/**
 * DD Chat Input Component
 *
 * Input field for sending messages to the DD assistant.
 * Supports file attachments for document analysis.
 */

import React, { memo, useCallback, useRef } from 'react';
import { View, Text, TextInput, Pressable, Keyboard } from 'react-native';
import { reportError } from '@/lib/errors';
import Animated, { useAnimatedStyle, withSpring, FadeIn, FadeOut } from 'react-native-reanimated';
import { Send, Sparkles, Paperclip, X, FileText } from 'lucide-react-native';
import * as DocumentPicker from 'expo-document-picker';
import { cn } from '@/lib/cn';
import { useThemeColors } from '@/lib/useThemeColors';
import type { DDChatInputProps, DDChatAttachment } from '@/lib/dd-chat/types';
import AnimatedPressable from '@/components/AnimatedPressable';

function DDChatInputComponent({
  value,
  onChangeText,
  onSubmit,
  disabled = false,
  placeholder = 'Ask about this startup...',
  autoFocus = false,
  allowAttachment = false,
  attachment,
  onAttach,
  onRemoveAttachment,
}: DDChatInputProps) {
  const colors = useThemeColors();
  const inputRef = useRef<TextInput>(null);
  const canSubmit = (value.trim().length > 0 || attachment) && !disabled;

  const handleSubmit = useCallback(() => {
    if (canSubmit) {
      onSubmit();
      Keyboard.dismiss();
    }
  }, [canSubmit, onSubmit]);

  const handlePickDocument = useCallback(async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: [
          'application/pdf',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation',
          'application/vnd.ms-powerpoint',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/msword',
          'text/plain',
        ],
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets?.length) {
        const asset = result.assets[0];
        onAttach?.({
          uri: asset.uri,
          name: asset.name,
          size: asset.size,
          mimeType: asset.mimeType,
        });
      }
    } catch (error) {
      reportError(error, { context: 'DDChatInput.handleAttach', level: 'warning' });
    }
  }, [onAttach]);

  // Animated button style
  const buttonStyle = useAnimatedStyle(() => {
    return {
      transform: [
        {
          scale: withSpring(canSubmit ? 1 : 0.9, {
            damping: 15,
            stiffness: 200,
          }),
        },
      ],
      opacity: withSpring(canSubmit ? 1 : 0.5),
    };
  }, [canSubmit]);

  // Format file size
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <View
      className="px-4 py-3 border-t"
      style={{ backgroundColor: colors.surface.primary, borderColor: colors.border.light }}
    >
      {/* Attachment Preview */}
      {attachment && (
        <Animated.View
          entering={FadeIn.duration(200)}
          exiting={FadeOut.duration(150)}
          className="mb-3 p-3 rounded-xl flex-row items-center gap-3"
          style={{ backgroundColor: colors.surface.secondary }}
        >
          <View
            className="w-10 h-10 rounded-lg items-center justify-center"
            style={{ backgroundColor: `${colors.primary[600]}15` }}
          >
            <FileText size={20} color={colors.primary[600]} />
          </View>
          <View className="flex-1">
            <Text
              style={{ color: colors.text.primary }}
              className="font-medium text-sm"
              numberOfLines={1}
            >
              {attachment.name}
            </Text>
            {attachment.size && (
              <Text style={{ color: colors.text.tertiary }} className="text-xs">
                {formatFileSize(attachment.size)}
              </Text>
            )}
          </View>
          <Pressable
            onPress={onRemoveAttachment}
            className="w-8 h-8 rounded-full items-center justify-center"
            style={{ backgroundColor: colors.surface.primary }}
          >
            <X size={16} color={colors.text.tertiary} />
          </Pressable>
        </Animated.View>
      )}

      {/* Input row */}
      <View className="flex-row items-end gap-2">
        {/* AI indicator */}
        <View
          className="w-8 h-8 rounded-full items-center justify-center mb-1"
          style={{ backgroundColor: `${colors.primary[600]}10` }}
        >
          <Sparkles size={16} color={colors.primary[600]} />
        </View>

        {/* Attachment button */}
        {allowAttachment && !attachment && (
          <AnimatedPressable
            onPress={handlePickDocument}
            disabled={disabled}
            className="w-10 h-10 rounded-full items-center justify-center mb-1"
            style={{ backgroundColor: colors.surface.secondary }}
          >
            <Paperclip size={18} color={colors.text.secondary} />
          </AnimatedPressable>
        )}

        {/* Input field */}
        <View
          className="flex-1 rounded-2xl border"
          style={{ backgroundColor: colors.surface.secondary, borderColor: colors.border.light }}
        >
          <TextInput
            ref={inputRef}
            value={value}
            onChangeText={onChangeText}
            onSubmitEditing={handleSubmit}
            placeholder={attachment ? 'Add a question about this document...' : placeholder}
            placeholderTextColor={colors.text.tertiary}
            multiline
            maxLength={2000}
            autoFocus={autoFocus}
            editable={!disabled}
            returnKeyType="send"
            blurOnSubmit={false}
            className={cn('px-4 py-3 text-base max-h-32', disabled && 'opacity-50')}
            style={{ color: colors.text.primary, textAlignVertical: 'center' }}
          />
        </View>

        {/* Send button */}
        <Animated.View style={[buttonStyle, { marginBottom: 4 }]}>
          <AnimatedPressable
            onPress={handleSubmit}
            disabled={!canSubmit}
            className="w-10 h-10 rounded-full items-center justify-center"
          >
            <View
              className="w-10 h-10 rounded-full items-center justify-center"
              style={{ backgroundColor: canSubmit ? colors.primary[600] : colors.surface.secondary }}
            >
              <Send
                size={18}
                color={canSubmit ? '#ffffff' : colors.text.tertiary}
                style={{ marginLeft: 2 }}
              />
            </View>
          </AnimatedPressable>
        </Animated.View>
      </View>

      {/* Character count */}
      {value.length > 1500 && (
        <View className="mt-1 px-10">
          <Text
            className={cn('text-xs', value.length > 1900 ? 'text-red-400' : 'text-zinc-500')}
            style={{ color: value.length > 1900 ? colors.error : colors.text.tertiary }}
          >
            {value.length}/2000
          </Text>
        </View>
      )}
    </View>
  );
}

export const DDChatInput = memo(DDChatInputComponent);
